import React from 'react';
import { Link } from 'react-router';
import { Store, ArrowRight, ShoppingBasket } from 'lucide-react';

import { Card } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { useIsMobile } from '@shared/hooks/use-media-query';

import { Role } from '@features/user-access/types';
import { useUser } from '@features/user-access/providers/user-provider';

const formatToday = () => {
  const label = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date());
  return label.charAt(0).toUpperCase() + label.slice(1);
};

type StatProps = {
  icon: React.ReactNode;
  value: number;
  label: string;
};

const Stat = ({ icon, value, label }: StatProps) => (
  <div className="flex items-center gap-3 rounded-lg border border-border px-4 py-2.5">
    <div aria-hidden className="flex text-muted-foreground [&>svg]:size-5">
      {icon}
    </div>
    <div>
      <p className="text-lg leading-tight font-bold tabular-nums">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  </div>
);

export const WelcomeBanner = () => {
  const { userAccess } = useUser();
  const shops = userAccess.shops ?? [];

  const totalOrders = shops.reduce((acc, shop) => acc + (shop.orderCount ?? 0), 0);
  const activeShops = shops.filter((shop) => shop.orderCount > 0).length;
  const isMobile = useIsMobile();
  const roles = userAccess.warehouse?.roles ?? [];
  // Phones scan (picking session); desktops print paper picking lists first, then fall back to scan.
  const pickingTargets = [
    { role: Role.Picking, href: '/warehouse/picking', label: 'Imprimer le picking' },
    { role: Role.PickingSession, href: '/warehouse/picking-session', label: 'Démarrer le picking scan' },
  ].filter((target) => !isMobile || target.role === Role.PickingSession);
  const pickingCta = pickingTargets.find((target) => roles.includes(target.role));

  return (
    <Card className="border border-border p-5 shadow-none md:p-8">
      <div className="flex max-w-180 flex-col gap-6">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{formatToday()}</span>
          <h1 className="text-2xl leading-tight font-bold text-balance md:text-3xl">Bonjour {userAccess.name}</h1>
        </div>

        {shops.length > 0 && (
          <div className="flex flex-col flex-wrap gap-3 sm:flex-row sm:items-center">
            <Stat
              icon={<ShoppingBasket />}
              label={totalOrders > 1 ? 'commandes à préparer' : 'commande à préparer'}
              value={totalOrders}
            />
            <Stat
              icon={<Store />}
              label={`boutique${activeShops > 1 ? 's' : ''} sur ${shops.length} avec des commandes`}
              value={activeShops}
            />
            {pickingCta && totalOrders > 0 && (
              <Button asChild size="lg">
                <Link to={pickingCta.href}>
                  {pickingCta.label}
                  <ArrowRight aria-hidden />
                </Link>
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
